import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socketServiceManager from 'app/socketService';
import { DealerLaunchSocketService } from 'app/socketService/dealerLaunch/dealerLaunchSocketService';
import { headerActions } from 'app/pages/Header/slice';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import type {
  DealerLaunchSocketEventLogEntry,
  DealerLaunchSocketEventName,
} from 'app/pages/Header/types/dealerLaunchProgress';
import {
  extractDealerLaunchSocketMessage,
  isDealerLaunchInProgressForDealerId,
  isDealerLaunchMaterializationInProgress,
  mergeLaunchCompletedIntoDealer,
  mergeLaunchProgressIntoDealer,
  mergeLaunchRequestReceivedIntoDealer,
  persistSelectedDealerToLocalStorage,
  readPersistedSelectedDealer,
  resolvePersistedDealerId,
} from 'utils/dealerLaunchMaterialization';

function getDefaultSocketConfig() {
  const socketUrl =
    process.env.REACT_APP_WS_URL ||
    process.env.REACT_APP_API_URL ||
    'http://localhost:3000';

  return {
    url: socketUrl,
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  };
}

function isDealerLaunchSocketEventName(
  eventName: string,
): eventName is DealerLaunchSocketEventName {
  return (
    eventName === 'dealer_launch_request_received' ||
    eventName === 'dealer_launch_progress' ||
    eventName === 'dealer_launch_completed'
  );
}

function buildEventLogEntry(
  eventName: DealerLaunchSocketEventName,
  data: unknown,
): DealerLaunchSocketEventLogEntry | null {
  const message = extractDealerLaunchSocketMessage<Record<string, unknown>>(data);
  if (!message) {
    return null;
  }

  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    timestamp: new Date().toISOString(),
    eventName,
    message,
  };
}

type UseDealerLaunchSocketSubscriptionOptions = {
  onSocketEvent?: (entry: DealerLaunchSocketEventLogEntry) => void;
  onLaunchRequestReceived?: (jobId: string | null) => void;
  onLaunchCompleted?: (status: 'COMPLETED' | 'FAILED', data: unknown) => void;
};

export function useDealerLaunchSocketSubscription(
  dealerId: string | null,
  options: UseDealerLaunchSocketSubscriptionOptions = {},
): void {
  const dispatch = useDispatch();
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerRef = useRef(selectedDealer);
  const serviceRef = useRef<DealerLaunchSocketService | null>(null);
  const previousDealerIdRef = useRef<string | null>(null);
  const activeCallbackRef = useRef<
    ((eventName: string, data: unknown) => void) | null
  >(null);
  const onSocketEventRef = useRef(options.onSocketEvent);
  const onLaunchRequestReceivedRef = useRef(options.onLaunchRequestReceived);
  const onLaunchCompletedRef = useRef(options.onLaunchCompleted);

  selectedDealerRef.current = selectedDealer;
  onSocketEventRef.current = options.onSocketEvent;
  onLaunchRequestReceivedRef.current = options.onLaunchRequestReceived;
  onLaunchCompletedRef.current = options.onLaunchCompleted;

  const launchInProgressInRedux =
    isDealerLaunchMaterializationInProgress(selectedDealer);
  const launchInProgressInStorage = isDealerLaunchInProgressForDealerId(dealerId);
  const shouldSubscribe =
    Boolean(dealerId) && (launchInProgressInRedux || launchInProgressInStorage);

  useEffect(() => {
    if (!dealerId || launchInProgressInRedux) {
      return;
    }

    const persisted = readPersistedSelectedDealer();
    const persistedId = resolvePersistedDealerId(persisted);
    if (
      persistedId === dealerId &&
      persisted &&
      isDealerLaunchMaterializationInProgress(persisted)
    ) {
      dispatch(headerActions.selectDealer(persisted));
    }
  }, [dealerId, dispatch, launchInProgressInRedux]);

  useEffect(() => {
    if (!shouldSubscribe || !dealerId) {
      return undefined;
    }

    const socketConfig = getDefaultSocketConfig();
    socketServiceManager.initializeGlobalSocket(socketConfig);

    const service = DealerLaunchSocketService.getInstance(socketConfig);
    socketServiceManager.registerService(service);
    service.connect();
    serviceRef.current = service;

    const callback = (eventName: string, data: unknown) => {
      if (isDealerLaunchSocketEventName(eventName)) {
        const entry = buildEventLogEntry(eventName, data);
        if (entry) {
          onSocketEventRef.current?.(entry);
        }

        if (eventName === 'dealer_launch_request_received') {
          const jobId =
            extractDealerLaunchSocketMessage<{ job_id?: string }>(data)?.job_id?.trim() ||
            null;
          onLaunchRequestReceivedRef.current?.(jobId);
        }
      }

      const currentDealer = selectedDealerRef.current;
      let nextDealer: Record<string, unknown> | null = null;

      if (eventName === 'dealer_launch_request_received') {
        nextDealer = mergeLaunchRequestReceivedIntoDealer(currentDealer, data);
      } else if (eventName === 'dealer_launch_progress') {
        nextDealer = mergeLaunchProgressIntoDealer(currentDealer, data);
      } else if (eventName === 'dealer_launch_completed') {
        nextDealer = mergeLaunchCompletedIntoDealer(currentDealer, data);
      }

      if (!nextDealer) {
        return;
      }

      dispatch(headerActions.selectDealer(nextDealer));
      persistSelectedDealerToLocalStorage(nextDealer);

      if (eventName === 'dealer_launch_completed') {
        const status = String(
          (data as { message?: { status?: string } })?.message?.status ??
            (data as { status?: string })?.status ??
            '',
        )
          .trim()
          .toUpperCase();

        if (status === 'FAILED') {
          const errorMessage =
            (data as { message?: { error?: string } })?.message?.error?.trim() ||
            (data as { error?: string })?.error?.trim();
          if (errorMessage) {
            dispatch(
              notificationAlertActions.addNotification({
                open: true,
                severity: 'error',
                message: errorMessage,
              }),
            );
          }
        }

        if (status === 'COMPLETED' || status === 'FAILED') {
          onLaunchCompletedRef.current?.(status, data);
        }
      }
    };

    activeCallbackRef.current = callback;

    const previousDealerId = previousDealerIdRef.current;
    if (previousDealerId && previousDealerId !== dealerId && activeCallbackRef.current) {
      service.unsubscribeFromDealer(previousDealerId, activeCallbackRef.current);
    }

    service.subscribeToDealer(dealerId, callback);
    previousDealerIdRef.current = dealerId;

    return () => {
      if (serviceRef.current && dealerId && activeCallbackRef.current) {
        serviceRef.current.unsubscribeFromDealer(dealerId, activeCallbackRef.current);
      }
      if (previousDealerIdRef.current === dealerId) {
        previousDealerIdRef.current = null;
      }
      activeCallbackRef.current = null;
    };
  }, [dealerId, dispatch, shouldSubscribe]);
}
